import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

export default function Login() {
  const { user, login } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // Default landing page is the dashboard. The old default ('/?mode=practice')
  // dropped the student straight into a writing exam attempt right after
  // login, which was disorienting and could even start a free attempt.
  // Honour an explicit ?next= so deep-links keep working.
  const next = new URLSearchParams(location.search).get('next') || '/dashboard';
  const theme = isDark ? {
    bg: 'radial-gradient(circle at center, #1e1b4b 0%, #111827 46%, #020617 100%)',
    dot: 'rgba(129,140,248,0.12)',
    orb: 'rgba(99,102,241,0.16)',
    card: '#0f172a',
    border: '#1f2937',
    shadow: '0 8px 32px rgba(15,23,42,0.36)',
    title: '#F8FAFC',
    body: '#94A3B8',
    accent: '#818CF8',
    googleBg: '#111827',
    googleHover: '#172033',
    googleText: '#F8FAFC',
    googleBorder: '#334155',
    back: '#94A3B8',
    trust: '#64748B',
    iconBg: '#6366F1',
  } : {
    bg: 'radial-gradient(circle at center, #EEF2FF 0%, #F5F3FF 42%, #F9FAFB 100%)',
    dot: 'rgba(79,70,229,0.06)',
    orb: 'rgba(199,210,254,0.15)',
    card: '#ffffff',
    border: '#E5E7EB',
    shadow: '0 4px 24px rgba(79,70,229,0.07)',
    title: '#111827',
    body: '#6B7280',
    accent: '#4F46E5',
    googleBg: '#fff',
    googleHover: '#F9FAFB',
    googleText: '#111827',
    googleBorder: '#E5E7EB',
    back: '#9CA3AF',
    trust: '#9CA3AF',
    iconBg: '#4F46E5',
  };

  useEffect(() => {
    if (user) navigate(next, { replace: true });
  }, [navigate, next, user]);

  const handleSuccess = async (credentialResponse) => {
    try {
      await login(credentialResponse.credential);
      navigate(next, { replace: true });
    } catch (error) {
      alert(error?.response?.data?.error || 'Login failed. Please try again.');
      console.error(error);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.bg,
        fontFamily: "'Inter', 'Outfit', sans-serif",
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Outfit:wght@400;500;600;700;800&display=swap');

        .fet-login-shell::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle, ${theme.dot} 1px, transparent 1px);
          background-size: 20px 20px;
          opacity: 0.4;
          pointer-events: none;
        }

        .fet-login-shell::after {
          content: '';
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            radial-gradient(circle at 15% 20%, ${theme.orb} 0, ${theme.orb} 160px, transparent 320px),
            radial-gradient(circle at 85% 78%, ${theme.orb} 0, ${theme.orb} 160px, transparent 320px);
          filter: blur(80px);
        }

        .fet-login-card {
          position: relative;
          z-index: 1;
        }

        .fet-back-link {
          transition: all 150ms ease;
        }

        .fet-back-link:hover {
          color: ${isDark ? '#CBD5E1' : '#6B7280'} !important;
        }
      `}</style>

      <div
        className="fet-login-shell"
        style={{
          position: 'absolute',
          inset: 0,
        }}
      />

      <div
        className="fet-login-card"
        style={{
          width: '100%',
          maxWidth: '440px',
          background: theme.card,
          border: `1px solid ${theme.border}`,
          borderRadius: '20px',
          padding: '48px 40px',
          boxShadow: theme.shadow,
          textAlign: 'center',
        }}
      >
        <button
          onClick={toggleTheme}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            position: 'absolute',
            top: '18px',
            right: '18px',
            border: `1px solid ${theme.border}`,
            background: isDark ? '#111827' : '#ffffff',
            color: theme.body,
            borderRadius: '999px',
            width: '38px',
            height: '38px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          {isDark ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="4.5" stroke="currentColor" strokeWidth="1.8" />
              <path d="M12 2.75V5.25M12 18.75V21.25M4.75 12H2.75M21.25 12H19.25M5.64 5.64L7.41 7.41M16.59 16.59L18.36 18.36M18.36 5.64L16.59 7.41M7.41 16.59L5.64 18.36" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5a8.5 8.5 0 1 0 11 11Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
        <div
          style={{
            width: '40px',
            height: '40px',
            margin: '0 auto 0',
            borderRadius: '10px',
            background: theme.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: isDark ? '0 10px 24px rgba(99,102,241,0.22)' : '0 10px 24px rgba(79,70,229,0.18)'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 18L18 6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
            <path d="M8 6h10v10" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <div style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '1.5px', textTransform: 'uppercase', color: theme.accent, marginTop: 0, marginBottom: 12 }}>
          FET EXAM PREP
        </div>

        <h1 style={{ fontSize: '28px', fontWeight: 700, color: theme.title, margin: '0 0 12px', lineHeight: 1.3 }}>
          Sign in to start practice
        </h1>

        <p style={{ fontSize: '14px', color: theme.body, lineHeight: 1.7, margin: '0 auto 28px', maxWidth: '320px' }}>
          Use the same account and backend as the main FET platform. Once you sign in, you can continue directly into the FET writing flow.
        </p>

        <div style={{ display: 'grid', gap: '10px', marginBottom: '20px' }}>
          <div
            style={{
              width: '100%',
              borderRadius: '10px',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <GoogleLogin
              onSuccess={handleSuccess}
              onError={() => alert('Google login failed. Check your Client ID and authorized domain settings.')}
              text="continue_with"
              theme={isDark ? 'filled_black' : 'outline'}
              size="large"
              shape="rectangular"
              width="360"
              logo_alignment="left"
            />
          </div>

          <button
            className="fet-back-link"
            onClick={() => navigate('/')}
            style={{
              background: 'transparent',
              border: 'none',
              color: theme.back,
              fontSize: '13px',
              cursor: 'pointer',
              textAlign: 'center',
              padding: '0',
              marginTop: '16px',
            }}
          >
            ← Back to FET landing page
          </button>
        </div>

        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            fontSize: '11px',
            color: theme.trust,
            lineHeight: 1.5,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M7 11V8a5 5 0 0 1 10 0v3" stroke={theme.trust} strokeWidth="2" strokeLinecap="round" />
            <rect x="5" y="11" width="14" height="9" rx="2" stroke={theme.trust} strokeWidth="2" />
          </svg>
          <span>Secured by Google OAuth · No password stored</span>
        </div>
      </div>
    </div>
  );
}
